import { useEffect, useMemo, useState } from 'react';
import { Button, Chip, Select, TextField } from '@nyce/admin-kit/ui';
import type { Cluster, DbServer, Environment, EnvironmentInput, EnvironmentKind } from '../../api';
import { api } from '../../session';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  TagsField,
  useAsync,
  useNotice,
} from '../../components/common';
import {
  EditorDialog,
  EnvChip,
  IdField,
  SLUG,
  SwitchField,
  byName,
  errorText,
  isDnsLabel,
  useEditorForm,
} from '../../components/registry';
import { TenantPicker } from '../../identity/TenantPicker';
import { useOnboardingConfig } from '../../onboarding/api/config';

type Form = EnvironmentInput & { id: string };

const empty: Form = {
  id: '',
  name: '',
  customer: '',
  kind: 'production',
  clusterId: null,
  namespaces: [],
  dbServerId: null,
  dbName: '',
  baseUrl: '',
  notes: '',
  enabled: true,
  authTenantId: '',
};

const toForm = ({ updatedAt: _u, clusterName: _c, dbServerName: _d, ...e }: Environment): Form => ({
  ...e,
  namespaces: e.namespaces ?? [],
  dbName: e.dbName ?? '',
  baseUrl: e.baseUrl ?? '',
  notes: e.notes ?? '',
  authTenantId: e.authTenantId ?? '',
});

const KINDS: { value: EnvironmentKind; label: string }[] = [
  { value: 'production', label: 'Production' },
  { value: 'test', label: 'Test' },
  { value: 'staging', label: 'Staging' },
  { value: 'dev', label: 'Dev' },
];

const NONE = '';
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Client-side mirror of the API's rules, by field; empty when the form may be saved. */
export function environmentProblems(v: Form): Record<string, string> {
  const p: Record<string, string> = {};
  if (!SLUG.test(v.id)) p.id = 'id';
  if (!v.name.trim()) p.name = 'required';
  const bad = v.namespaces.filter((n) => !isDnsLabel(n));
  if (bad.length) p.namespaces = `Not a valid namespace (DNS label): ${bad.join(', ')}`;
  else if (v.clusterId && !v.namespaces.length) p.namespaces = 'At least one namespace when a cluster is set';
  if (v.dbServerId && !v.dbName.trim()) p.dbName = 'Pick or type the database';
  if (v.baseUrl && !/^https?:\/\/\S+$/.test(v.baseUrl))
    p.baseUrl = 'absolute URL, e.g. https://customer.nycelogic.com';
  if (v.authTenantId?.trim() && !GUID.test(v.authTenantId.trim()))
    p.authTenantId = 'an AUTH tenant id (GUID)';
  return p;
}

type Dbs = { loading: boolean; list: string[] | null; error: string | null };

function DatabaseField({
  serverId,
  value,
  onChange,
  error,
}: {
  serverId: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  const [dbs, setDbs] = useState<Dbs>({ loading: true, list: null, error: null });
  useEffect(() => {
    let live = true;
    setDbs({ loading: true, list: null, error: null });
    api.admin.dbServers
      .databases(serverId)
      .then((list) => live && setDbs({ loading: false, list, error: null }))
      .catch((e) => live && setDbs({ loading: false, list: null, error: errorText(e) }));
    return () => {
      live = false;
    };
  }, [serverId]);

  if (dbs.list) {
    const names = value && !dbs.list.includes(value) ? [value, ...dbs.list] : dbs.list;
    return (
      <div className="nyk-ui-field">
        <Select
          label="Database"
          value={value}
          onChange={onChange}
          placeholder={names.length ? 'Pick a database' : 'No databases on this server'}
          options={names.map((n) => ({ value: n, label: n }))}
        />
        {error && <span className="nyk-ui-input-error">{error}</span>}
      </div>
    );
  }
  return (
    <TextField
      label="Database"
      value={value}
      onChange={(e) => onChange(e.target.value.trim())}
      disabled={dbs.loading}
      placeholder={dbs.loading ? 'loading…' : ''}
      error={error && !dbs.loading ? error : undefined}
      hint={dbs.error ? `Could not list databases (${dbs.error}); type the name` : undefined}
    />
  );
}

function EnvironmentForm({
  initial,
  isNew,
  clusters,
  servers,
  onSaved,
  onClose,
}: {
  initial: Form;
  isNew: boolean;
  clusters: Cluster[];
  servers: DbServer[];
  onSaved: (e: Environment) => void;
  onClose: () => void;
}) {
  const f = useEditorForm<Form>(initial, isNew);
  const { v, set } = f;
  const hubTarget = useOnboardingConfig()?.hubTarget;
  const problems = environmentProblems(v);
  const canSave = !Object.keys(problems).length;
  const save = () =>
    void f.run(async () => {
      const { id, ...body } = v;
      onSaved(
        await api.admin.environments.put(id, {
          ...body,
          name: body.name.trim(),
          customer: body.customer.trim(),
          dbName: body.dbServerId ? body.dbName.trim() : '',
          baseUrl: body.baseUrl.trim(),
          authTenantId: body.authTenantId?.trim() || null,
        })
      );
    });
  const clusterOptions = [
    { value: NONE, label: '— none —' },
    ...[...clusters].sort(byName).map((c) => ({
      value: c.id,
      label: `${c.name || c.id} (${c.environment})${c.enabled ? '' : ' – disabled'}`,
    })),
  ];
  const serverOptions = [
    { value: NONE, label: '— none —' },
    ...[...servers].sort(byName).map((s) => ({
      value: s.id,
      label: `${s.name || s.id} (${s.environment})${s.enabled ? '' : ' – disabled'}`,
    })),
  ];
  // Keep a reference that no longer exists visible instead of silently showing "none".
  if (v.clusterId && !clusters.some((c) => c.id === v.clusterId))
    clusterOptions.push({ value: v.clusterId, label: `${v.clusterId} (unknown)` });
  if (v.dbServerId && !servers.some((s) => s.id === v.dbServerId))
    serverOptions.push({ value: v.dbServerId, label: `${v.dbServerId} (unknown)` });

  return (
    <EditorDialog
      title={isNew ? 'New environment' : `Environment ${v.id}`}
      error={f.error}
      busy={f.busy}
      canSave={canSave}
      onSave={save}
      onClose={onClose}
      maxWidth={760}
    >
      <TextField label="Name" value={v.name} onChange={(e) => set('name', e.target.value)} required />
      <IdField value={v.id} isNew={isNew} onChange={f.setId} example="acme-prod" />
      <TextField label="Customer" value={v.customer} onChange={(e) => set('customer', e.target.value)} />
      <Select
        label="Kind"
        value={v.kind}
        onChange={(k) => set('kind', k as EnvironmentKind)}
        options={KINDS}
      />
      <Select
        label="Cluster"
        value={v.clusterId ?? NONE}
        onChange={(c) => set('clusterId', c || null)}
        options={clusterOptions}
      />
      <div className="nyk-span2">
        <TagsField
          label="Namespaces"
          value={v.namespaces}
          onChange={(n) => set('namespaces', n)}
          placeholder="e.g. acme-prod, Enter to add"
          hint={problems.namespaces ? undefined : 'Kubernetes namespaces of this installation'}
        />
        {problems.namespaces && (
          <span className="nyk-ui-input-error" role="alert" data-error="namespaces">
            {problems.namespaces}
          </span>
        )}
      </div>
      <Select
        label="DB server"
        value={v.dbServerId ?? NONE}
        onChange={(s) => setDbServer(s || null)}
        options={serverOptions}
      />
      {v.dbServerId ? (
        <DatabaseField
          serverId={v.dbServerId}
          value={v.dbName}
          onChange={(d) => set('dbName', d)}
          error={problems.dbName}
        />
      ) : (
        <div />
      )}
      <TextField
        label="Base URL"
        className="nyk-url"
        value={v.baseUrl}
        onChange={(e) => set('baseUrl', e.target.value)}
        error={problems.baseUrl}
        placeholder="https://"
        hint="where the installation answers"
      />
      <TenantPicker
        target={hubTarget}
        value={v.authTenantId ?? ''}
        onChange={(id) => set('authTenantId', id)}
        error={problems.authTenantId}
        hint="the installation's tenant in the hub's AUTH: links Statistics to this environment"
      />
      <SwitchField label="Enabled" checked={v.enabled} onChange={(e) => set('enabled', e)} />
      <div className="nyk-span2">
        <TextField
          label="Notes"
          multiline
          rows={3}
          value={v.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </div>
    </EditorDialog>
  );

  function setDbServer(id: string | null) {
    f.setV((cur) => ({ ...cur, dbServerId: id, dbName: id === cur.dbServerId ? cur.dbName : '' }));
  }
}

function Namespaces({ list }: { list: string[] }) {
  const shown = list.slice(0, 3);
  return (
    <span className="nyk-inline">
      {shown.map((n) => (
        <Chip key={n} size="sm">
          {n}
        </Chip>
      ))}
      {list.length > 3 && (
        <Chip size="sm" className="oph-more" title={list.slice(3).join(', ')}>
          +{list.length - 3}
        </Chip>
      )}
    </span>
  );
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

/** A registered environment prefilled from a discovered namespace (same cluster, namespace, guessed kind). */
export function formFromNamespace(e: Environment): Form {
  return {
    ...empty,
    id: slug(e.namespaces[0] ?? e.name),
    name: e.name,
    kind: e.kind,
    clusterId: e.clusterId,
    namespaces: e.namespaces,
  };
}

export function EnvironmentsPage() {
  const list = useAsync(() => api.admin.environments.list(), []);
  const discovered = useAsync(() => api.environments().then((l) => l.filter((e) => e.discovered)), []);
  const [showDiscovered, setShowDiscovered] = useState(false);
  const refs = useAsync(
    () => Promise.all([api.admin.clusters.list(), api.admin.dbServers.list()]).then(([c, s]) => ({ c, s })),
    []
  );
  const [editing, setEditing] = useState<{ value: Form; isNew: boolean } | null>(null);
  const [filter, setFilter] = useState('');
  const notice = useNotice();

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = (list.data ?? []).filter(
      (e) =>
        !q || e.name.toLowerCase().includes(q) || e.customer.toLowerCase().includes(q) || e.id.includes(q)
    );
    const by = new Map<string, Environment[]>();
    for (const e of rows) by.set(e.customer || '', [...(by.get(e.customer || '') ?? []), e]);
    return [...by.entries()]
      .sort(([a], [b]) => (!a ? 1 : !b ? -1 : a.localeCompare(b, undefined, { sensitivity: 'base' })))
      .map(([customer, envs]) => ({ customer, envs: envs.sort(byName) }));
  }, [list.data, filter]);

  const remove = async (e: Environment) => {
    try {
      await api.admin.environments.remove(e.id);
      notice.success(`Deleted ${e.name || e.id}`);
      await list.reload();
    } catch (err) {
      notice.error(`Could not delete ${e.name || e.id}: ${errorText(err)}`);
    }
  };

  return (
    <Page
      title="Environments"
      description="Every namespace of every cluster is an environment already. Register one to group namespaces, name the customer, or link its database."
      actions={
        <>
          <input
            className="nyk-ui-input nyk-search"
            type="search"
            aria-label="Filter environments"
            placeholder="Filter name or customer"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Button onClick={() => setEditing({ value: { ...empty }, isNew: true })} disabled={!refs.data}>
            Add
          </Button>
        </>
      }
    >
      <ErrorBox error={list.error || refs.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !list.data.length && (
        <EmptyState
          card
          title="No registered environments"
          hint="Namespaces show up as environments on their own; register one to enrich it."
        />
      )}
      {list.data && !!list.data.length && !groups.length && (
        <EmptyState card title={`Nothing matches “${filter}”`} />
      )}
      {groups.map((g) => (
        <div className="nyk-card" key={g.customer} data-customer={g.customer}>
          <div className="nyk-card-head">
            <h2>{g.customer || 'No customer'}</h2>
            <span className="nyk-ui-hint">
              {g.envs.length} environment{g.envs.length === 1 ? '' : 's'}
            </span>
          </div>
          <TableScroller>
            <table className="nyk-ui-table nyk-cards">
              <thead>
                <tr>
                  <th>Environment</th>
                  <th>Customer</th>
                  <th>Kind</th>
                  <th>Cluster</th>
                  <th>Namespaces</th>
                  <th>Database</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {g.envs.map((e) => (
                  <tr key={e.id} data-row={e.id}>
                    <td className="nyk-card-title">
                      <b>{e.name || e.id}</b>
                      <div className="nyk-ui-hint">{e.id}</div>
                      {!e.enabled && (
                        <Chip size="sm" className="oph-off">
                          disabled
                        </Chip>
                      )}
                    </td>
                    <td data-label="Customer">{e.customer || '—'}</td>
                    <td data-label="Kind">
                      <EnvChip env={e.kind} />
                    </td>
                    <td data-label="Cluster">{e.clusterId ? e.clusterName || e.clusterId : '—'}</td>
                    <td data-label="Namespaces">
                      <Namespaces list={e.namespaces ?? []} />
                    </td>
                    <td data-label="Database" className="nyk-url">
                      {e.dbServerId ? `${e.dbServerName || e.dbServerId} / ${e.dbName}` : '—'}
                    </td>
                    <td className="nyk-row-actions">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ value: toForm(e), isNew: false })}
                        disabled={!refs.data}
                      >
                        Edit
                      </Button>
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        message={`Delete environment ${e.name || e.id}?`}
                        onConfirm={() => void remove(e)}
                      >
                        Delete
                      </ConfirmButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      ))}
      {!!discovered.data?.length && (
        <div className="nyk-card" data-testid="discovered-namespaces">
          <div className="nyk-card-head">
            <h2>
              Unregistered namespaces <span className="nyk-ui-hint">{discovered.data.length}</span>
            </h2>
            <Button size="sm" variant="ghost" onClick={() => setShowDiscovered((v) => !v)}>
              {showDiscovered ? 'Hide' : 'Show'}
            </Button>
          </div>
          <p className="nyk-card-intro">
            These already appear in Pods, Logs and the dashboard under their namespace name. Register one to
            add a customer, a database or more namespaces.
          </p>
          {showDiscovered && (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards">
                <thead>
                  <tr>
                    <th>Namespace</th>
                    <th>Cluster</th>
                    <th>Kind (guessed)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {discovered.data.map((e) => (
                    <tr key={e.id} data-namespace={e.id}>
                      <td className="nyk-card-title nyk-mono">{e.namespaces[0]}</td>
                      <td data-label="Cluster">{e.clusterName ?? e.clusterId}</td>
                      <td data-label="Kind">{e.kind}</td>
                      <td className="nyk-row-actions">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!refs.data}
                          onClick={() => setEditing({ value: formFromNamespace(e), isNew: true })}
                        >
                          Register
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          )}
        </div>
      )}
      {editing && refs.data && (
        <EnvironmentForm
          initial={editing.value}
          isNew={editing.isNew}
          clusters={refs.data.c}
          servers={refs.data.s}
          onClose={() => setEditing(null)}
          onSaved={(e) => {
            setEditing(null);
            notice.success(`Saved ${e.name || e.id}`);
            void list.reload();
            void discovered.reload();
          }}
        />
      )}
      {notice.element}
    </Page>
  );
}
